import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Card, Input, formatCpf } from "@atlas/ui/web";
import { atlas } from "../lib/sdk";
import { AtlasLogo } from "../components/AtlasBrand";

/** Input de senha com botao "mostrar/ocultar". Wrapper simples em cima do
 *  Input do design system — sem precisar mexer no @atlas/ui. Cada campo tem
 *  seu proprio toggle (mais explicito que um switch global). */
function PasswordInput({
  label, value, onChange, autoComplete = "new-password",
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
}) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <Input
        label={label}
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        required
        style={{ paddingRight: 44 }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Ocultar senha" : "Mostrar senha"}
        title={show ? "Ocultar senha" : "Mostrar senha"}
        style={{
          position: "absolute", right: 8, bottom: 8,
          width: 32, height: 32, borderRadius: 8,
          border: "none", background: "transparent",
          color: "var(--text-muted)", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18, lineHeight: 1,
        }}
      >
        {show ? "🙈" : "👁"}
      </button>
    </div>
  );
}

type Step = "cpf" | "email" | "codigo" | "ok";

interface PrefeituraInfo {
  nome: string;
  cargo: string;
  matricula: string;
}

/** Master de consignataria ou de organizacao. Desde 09/10/2026 a averbadora
 *  nao cadastra a senha dele: ele se identifica aqui pelo e-mail de login, e o
 *  codigo vai para esse mesmo e-mail. */
interface MasterInfo {
  tipo: "banco" | "prefeitura";
  nome: string;
}

/** O campo da primeira etapa aceita os dois. `@` decide: CPF nunca tem. */
const ehEmail = (v: string) => v.includes("@");
const EMAIL_RE = /^\S+@\S+\.\S+$/;

// formatCpf agora vem de @atlas/ui/web (helper compartilhado).

// Formata (48) 99101-2233 (11 digitos) ou (48) 3234-5678 (10 digitos).
function formatTelefone(v: string): string {
  const d = v.replace(/\D/g, "").slice(0, 11);
  if (d.length <= 2) return d.length ? `(${d}` : "";
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/** Mensagem do modo DESENVOLVIMENTO, quando o backend devolve o codigo.
 *
 *  So e' chamada quando `codigo` existe — e ele so existe com APP_ENV dev/local.
 *  Em producao o envio que falha nao tem codigo nenhum, e o fluxo NAO avanca de
 *  passo: ver `enviarCodigo`. Antes esta funcao tratava os dois casos e o passo
 *  avancava sempre, deixando o usuario num campo que nada satisfazia. */
function avisoComCodigo(aviso: string | undefined, codigo: string): string {
  return `${aviso ?? "E-mail não enviado (ambiente de desenvolvimento)."} Use este código: ${codigo}`;
}

/** O que dizer quando o e-mail nao saiu e NAO ha codigo.
 *
 *  Aqui a mensagem pode ser especifica sem risco de enumeracao: o CPF ja foi
 *  validado no passo anterior e o e-mail foi digitado pelo proprio usuario. A
 *  acao de maior valor e' conferir o que ele digitou. */
function bloqueioPrimeiroAcesso(email: string, master = false): string {
  const ajuda = master
    ? "fale com a averbadora — a ativação depende deste e-mail de login."
    : "fale com o RH do seu órgão — a ativação depende deste e-mail.";
  return `Não conseguimos enviar o código para ${email}. Confira se o e-mail está escrito corretamente e tente de novo em alguns instantes. Se o erro continuar, ${ajuda}`;
}

export function PrimeiroAcessoPage() {
  const nav = useNavigate();
  const [step, setStep] = useState<Step>("cpf");
  /** CPF (servidor) ou e-mail de login (master). Ver `ehEmail`. */
  const [cpf, setCpf] = useState("");
  const [master, setMaster] = useState<MasterInfo | null>(null);
  const emailMaster = cpf.trim().toLowerCase();
  const [email, setEmail] = useState("");
  const [telefone, setTelefone] = useState("");
  const [senha, setSenha] = useState("");
  const [senha2, setSenha2] = useState("");
  const [codigo, setCodigo] = useState("");
  const [aceitouTermos, setAceitou] = useState(false);
  const [aceitouLgpd, setAceitouLgpd] = useState(false);

  /** Formulario de dados completo? Espelha as MESMAS regras do submit (email,
   *  telefone com DDD, senha forte, confirmacao e os dois aceites). Usado pra
   *  manter o botao desabilitado ate estar tudo preenchido. */
  const telDigitsUi = telefone.replace(/\D/g, "");
  const podeEnviarDados =
    /^\S+@\S+\.\S+$/.test(email)
    && telDigitsUi.length >= 10 && telDigitsUi.length <= 11
    && senha.length >= 8
    && /[a-zA-Z]/.test(senha) && /\d/.test(senha)
    && senha === senha2
    && aceitouTermos
    && aceitouLgpd;
  /** O master nao informa e-mail nem telefone: o e-mail e' o de login, e o
   *  contato dele ja esta no cadastro feito pela averbadora. */
  const podeEnviarDadosMaster =
    senha.length >= 8
    && /[a-zA-Z]/.test(senha) && /\d/.test(senha)
    && senha === senha2
    && aceitouTermos
    && aceitouLgpd;
  const [profile, setProfile] = useState<PrefeituraInfo | null>(null);
  const [destinoMasked, setDestinoMasked] = useState<string>("");
  const [avisoCodigo, setAvisoCodigo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function validarCpf(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (ehEmail(cpf)) {
        if (!EMAIL_RE.test(emailMaster)) { setError("Informe um e-mail válido."); return; }
        const r = await atlas.primeiroAcesso.master.buscar(emailMaster);
        if (!r.encontrado || !r.tipo) {
          setError("E-mail não encontrado entre os cadastros de consignatárias e órgãos. Confira o e-mail de login informado à averbadora.");
          return;
        }
        if (r.ja_tem_senha) {
          setError("Esta conta já foi ativada. Use 'Já tenho conta — entrar' abaixo. Se esqueceu a senha, use 'Esqueci minha senha'.");
          return;
        }
        if (!r.ativo) {
          setError("Este cadastro ainda não está ativo. Aguarde a liberação da averbadora para fazer o primeiro acesso.");
          return;
        }
        setProfile(null);
        setMaster({ tipo: r.tipo, nome: r.nome ?? "" });
        setStep("email");
        return;
      }
      const digits = cpf.replace(/\D/g, "");
      if (digits.length !== 11) { setError("CPF deve ter 11 dígitos."); return; }
      const r = await atlas.primeiroAcesso.buscar(digits);
      if (!r.encontrado) {
        setError("CPF não encontrado na base do seu órgão. Entre em contato com o RH para regularizar o cadastro.");
        return;
      }
      if (r.ja_tem_senha) {
        setError("Esta conta já foi ativada. Use 'Já tenho conta — entrar' abaixo. Se esqueceu a senha, use 'Esqueci minha senha'.");
        return;
      }
      setMaster(null);
      setProfile({ nome: r.nome ?? "", cargo: r.cargo ?? "", matricula: r.matricula ?? "" });
      setStep("email");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao verificar o cadastro.");
    } finally {
      setLoading(false);
    }
  }

  /** Pede o codigo do MASTER — sem e-mail nem telefone a informar. Mesma regra
   *  do servidor: sem como entregar o codigo, nao avanca de passo. */
  async function pedirCodigoMaster(reenvio: boolean): Promise<void> {
    const r = await atlas.primeiroAcesso.master.codigo(emailMaster, senha);
    if (!r.enviado) {
      if (!r.codigo_teste) {
        setAvisoCodigo(null);
        setError(bloqueioPrimeiroAcesso(emailMaster, true));
        return;
      }
      setAvisoCodigo(avisoComCodigo(r.aviso, r.codigo_teste));
    } else {
      setAvisoCodigo(reenvio ? "Código reenviado." : null);
    }
    setDestinoMasked(r.destino || emailMaster);
    setStep("codigo");
  }

  async function enviarCodigo(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (master) {
      if (senha.length < 8) { setError("A senha deve ter no mínimo 8 caracteres."); return; }
      if (!/[a-zA-Z]/.test(senha) || !/\d/.test(senha)) { setError("A senha deve conter letras e números."); return; }
      if (senha !== senha2) { setError("As senhas não conferem."); return; }
      if (!aceitouTermos || !aceitouLgpd) { setError("É preciso aceitar os Termos e a Política de Privacidade."); return; }
      setLoading(true);
      try {
        await pedirCodigoMaster(false);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Falha ao enviar o código.");
      } finally {
        setLoading(false);
      }
      return;
    }
    if (!EMAIL_RE.test(email)) { setError("Informe um e-mail válido."); return; }
    const telDigits = telefone.replace(/\D/g, "");
    if (telDigits.length < 10 || telDigits.length > 11) { setError("Informe um telefone com DDD (10 ou 11 dígitos)."); return; }
    if (senha.length < 8) { setError("A senha deve ter no mínimo 8 caracteres."); return; }
    if (!/[a-zA-Z]/.test(senha) || !/\d/.test(senha)) { setError("A senha deve conter letras e números."); return; }
    if (senha !== senha2) { setError("As senhas não conferem."); return; }
    if (!aceitouTermos || !aceitouLgpd) { setError("É preciso aceitar os Termos e a Política de Privacidade."); return; }
    setLoading(true);
    try {
      const digits = cpf.replace(/\D/g, "");
      const r = await atlas.primeiroAcesso.codigo(digits, email.trim().toLowerCase(), senha, telDigits);
      // NAO AVANCA sem ter como entregar o codigo. Em producao o backend nao
      // devolve mais o `codigo_teste`, entao `!enviado && !codigo_teste`
      // significa que nao existe numero nenhum para digitar — mandar a pessoa
      // para o passo do codigo seria um beco. Nao avancar e' seguro: quem grava
      // e' o passo seguinte (`primeiroAcesso.confirmar`), nao este.
      if (!r.enviado) {
        if (!r.codigo_teste) {
          setError(bloqueioPrimeiroAcesso(email.trim().toLowerCase()));
          return;
        }
        setAvisoCodigo(avisoComCodigo(r.aviso, r.codigo_teste));
      } else {
        setAvisoCodigo(null);
      }
      setDestinoMasked(r.destino || email);
      setStep("codigo");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao enviar o código.");
    } finally {
      setLoading(false);
    }
  }

  async function reenviarCodigo() {
    setError(null);
    setLoading(true);
    if (master) {
      try {
        await pedirCodigoMaster(true);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Falha ao reenviar.");
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const digits = cpf.replace(/\D/g, "");
      const telDigits = telefone.replace(/\D/g, "");
      const r = await atlas.primeiroAcesso.codigo(digits, email.trim().toLowerCase(), senha, telDigits);
      // Falha de reenvio vai para o ErrorBox, nao para o InfoBox neutro: sem
      // codigo, "aviso" com cara de informacao esconde que nada chegou.
      if (!r.enviado) {
        if (!r.codigo_teste) {
          setAvisoCodigo(null);
          setError(bloqueioPrimeiroAcesso(email.trim().toLowerCase()));
          return;
        }
        setAvisoCodigo(avisoComCodigo(r.aviso, r.codigo_teste));
      } else {
        setAvisoCodigo("Código reenviado.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao reenviar.");
    } finally {
      setLoading(false);
    }
  }

  async function confirmar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (codigo.replace(/\D/g, "").length !== 6) { setError("Código precisa ter 6 dígitos."); return; }
    setLoading(true);
    try {
      if (master) {
        await atlas.primeiroAcesso.master.confirmar(emailMaster, codigo.replace(/\D/g, ""));
      } else {
        await atlas.primeiroAcesso.confirmar(cpf.replace(/\D/g, ""), codigo.replace(/\D/g, ""));
      }
      setStep("ok");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Código inválido ou expirado.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (step !== "ok") return;
    const t = setTimeout(() => nav("/login"), 4000);
    return () => clearTimeout(t);
  }, [step, nav]);

  return (
    <div className="auth-shell">
      <Card style={{ background: "var(--surface)", maxWidth: 520 }}>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginBottom: 20, textAlign: "center" }}>
          <AtlasLogo height={72} />
          <div>
            <div style={{ fontWeight: 700, fontSize: "1.1rem" }}>Primeiro acesso</div>
            <div style={{ fontSize: ".82rem", color: "var(--text-dim)" }}>
              Ative sua conta a partir do cadastro feito pelo seu órgão ou pela averbadora
            </div>
          </div>
        </div>

        <Stepper step={step} />

        <div style={{ marginTop: 20 }}>
          {step === "cpf" ? (
            <form onSubmit={validarCpf} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <p style={{ color: "var(--text-muted)", fontSize: ".9rem", margin: 0 }}>
                <b>Servidor:</b> informe seu CPF. <b>Consignatária ou órgão/entidade:</b> informe o e-mail de login cadastrado na averbadora.
              </p>
              <Input
                label="CPF ou e-mail"
                type="text"
                value={cpf}
                // So formata como CPF o que pode ser CPF. Com letra ou `@`, e'
                // e-mail e passa cru — formatCpf descartaria tudo que nao e' digito.
                onChange={(e) => setCpf(/[a-zA-Z@]/.test(e.target.value) ? e.target.value.trim() : formatCpf(e.target.value))}
                placeholder="000.111.222-33 ou voce@empresa.com.br"
                autoComplete="off"
                maxLength={254}
                required
              />
              {error ? <ErrorBox>{error}</ErrorBox> : null}
              <Button type="submit" disabled={loading || !cpf}>
                {loading ? "Verificando..." : "Continuar →"}
              </Button>
            </form>
          ) : null}

          {step === "email" && master ? (
            <form onSubmit={enviarCodigo} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <InfoCard>
                <div style={{ fontWeight: 600 }}>{master.nome}</div>
                <div style={{ fontSize: ".85rem", color: "var(--text-muted)" }}>
                  {master.tipo === "banco" ? "Consignatária" : "Órgão/entidade"} · Usuário master
                </div>
              </InfoCard>
              <p style={{ color: "var(--text-muted)", fontSize: ".9rem", margin: 0 }}>
                Escolha a senha de acesso. Vamos enviar um código para <b style={{ color: "var(--text)" }}>{emailMaster}</b> para confirmar que o e-mail de login é seu.
              </p>
              <PasswordInput label="Nova senha" value={senha} onChange={setSenha} />
              <p
                style={{
                  margin: "-6px 0 0",
                  fontSize: ".76rem",
                  color: senha.length >= 8 ? "var(--emerald-500)" : "var(--text-dim)",
                }}
              >
                {senha.length >= 8 ? "✓ Senha com o tamanho mínimo." : "Use no mínimo 8 caracteres (letras e números)."}
              </p>
              <PasswordInput label="Confirmar senha" value={senha2} onChange={setSenha2} />
              <div
                style={{
                  maxHeight: 140, overflow: "auto", padding: 12,
                  background: "var(--bg-elev-2)", border: "1px solid var(--border)", borderRadius: 10,
                  fontSize: ".78rem", color: "var(--text-muted)", lineHeight: 1.55,
                }}
              >
                <b>Termos.</b> Ao ativar, você passa a responder pelo acesso da sua organização à plataforma Atlas, inclusive pelos usuários que cadastrar nela.
                <br /><b>LGPD.</b> Os dados pessoais acessados na plataforma são tratados conforme a Lei 13.709/2018, apenas para as finalidades do convênio.
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: ".85rem" }}>
                <input type="checkbox" checked={aceitouTermos} onChange={(e) => setAceitou(e.target.checked)} />
                Li e aceito os Termos de uso
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: ".85rem" }}>
                <input type="checkbox" checked={aceitouLgpd} onChange={(e) => setAceitouLgpd(e.target.checked)} />
                Concordo com a Política de Privacidade (LGPD)
              </label>
              {error ? <ErrorBox>{error}</ErrorBox> : null}
              <Button type="submit" disabled={loading || !podeEnviarDadosMaster}>
                {loading ? "Enviando..." : "Enviar código para meu e-mail →"}
              </Button>
              <button type="button" onClick={() => setStep("cpf")} style={linkBtn}>← Voltar</button>
            </form>
          ) : null}

          {step === "email" && profile ? (
            <form onSubmit={enviarCodigo} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <InfoCard>
                <div style={{ fontWeight: 600 }}>{profile.nome}</div>
                <div style={{ fontSize: ".85rem", color: "var(--text-muted)" }}>
                  {profile.cargo}{profile.matricula ? ` · Matrícula ${profile.matricula}` : ""}
                </div>
              </InfoCard>
              <p style={{ color: "var(--text-muted)", fontSize: ".9rem", margin: 0 }}>
                Informe um <b>e-mail seu</b> — pode ser pessoal, não precisa ser o institucional. Vamos enviar um código para confirmar que é seu.
                Escolha também uma senha para acessar a plataforma.
              </p>
              <Input
                label="Seu e-mail"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="você@gmail.com"
                autoComplete="email"
                required
              />
              <Input
                label="Seu telefone (com DDD)"
                type="tel"
                value={telefone}
                onChange={(e) => setTelefone(formatTelefone(e.target.value))}
                placeholder="(48) 99101-2233"
                autoComplete="tel"
                inputMode="tel"
                maxLength={16}
                required
              />
              <p style={{ margin: "-6px 0 0", fontSize: ".76rem", color: "var(--text-dim)" }}>
                Ficará no seu cadastro para que as instituições parceiras possam entrar em contato quando necessário.
              </p>
              <PasswordInput label="Nova senha" value={senha} onChange={setSenha} />
              {/* Aviso ANTES de digitar. A regra de 8 caracteres ja existia, mas
                  so aparecia como erro depois de enviar o formulario — o servidor
                  escolhia uma senha curta, era recusado e tinha que refazer.
                  Fica verde quando a senha alcanca o minimo. Cliente 07/08/2026. */}
              <p
                style={{
                  margin: "-6px 0 0",
                  fontSize: ".76rem",
                  color: senha.length >= 8 ? "var(--emerald-500)" : "var(--text-dim)",
                }}
              >
                {senha.length >= 8 ? "✓ Senha com o tamanho mínimo." : "Use no mínimo 8 caracteres (letras e números)."}
              </p>
              <PasswordInput label="Confirmar senha" value={senha2} onChange={setSenha2} />
              <div
                style={{
                  maxHeight: 140, overflow: "auto", padding: 12,
                  background: "var(--bg-elev-2)", border: "1px solid var(--border)", borderRadius: 10,
                  fontSize: ".78rem", color: "var(--text-muted)", lineHeight: 1.55,
                }}
              >
                <b>Termos.</b> Ao ativar, você autoriza a Atlas a consultar sua margem e intermediar averbações junto ao seu órgão e às instituições parceiras.
                <br /><b>LGPD.</b> Seus dados (nome, CPF, e-mail, telefone, matrícula, salário, contratos) são tratados conforme a Lei 13.709/2018. Você pode pedir exclusão a qualquer momento.
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: ".85rem" }}>
                <input type="checkbox" checked={aceitouTermos} onChange={(e) => setAceitou(e.target.checked)} />
                Li e aceito os Termos de uso
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10, fontSize: ".85rem" }}>
                <input type="checkbox" checked={aceitouLgpd} onChange={(e) => setAceitouLgpd(e.target.checked)} />
                Concordo com a Política de Privacidade (LGPD)
              </label>
              {error ? <ErrorBox>{error}</ErrorBox> : null}
              {/* Botao so libera com o formulario COMPLETO — inclusive os dois
                  aceites. Antes ficava clicavel e o erro so aparecia depois de
                  enviar; o usuario conseguia "prosseguir" sem marcar os
                  quadradinhos. Mesmas regras validadas no submit. Cliente 04/08/2026. */}
              <Button type="submit" disabled={loading || !podeEnviarDados}>
                {loading ? "Enviando..." : "Enviar código para meu e-mail →"}
              </Button>
              <button type="button" onClick={() => setStep("cpf")} style={linkBtn}>← Voltar</button>
            </form>
          ) : null}

          {step === "codigo" ? (
            <form onSubmit={confirmar} style={{ display: "flex", flexDirection: "column", gap: 14 }}>
              <p style={{ color: "var(--text-muted)", fontSize: ".9rem", margin: 0 }}>
                Enviamos um código de 6 dígitos para <b style={{ color: "var(--text)" }}>{destinoMasked}</b>. Ele expira em 10 minutos.
              </p>
              {avisoCodigo ? <InfoBox>{avisoCodigo}</InfoBox> : null}
              <Input
                label="Código de verificação"
                value={codigo}
                onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                placeholder="000000"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                required
              />
              {error ? <ErrorBox>{error}</ErrorBox> : null}
              <Button type="submit" disabled={loading || codigo.length < 6}>
                {loading ? "Confirmando..." : "Ativar minha conta"}
              </Button>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <button type="button" onClick={() => setStep("email")} style={linkBtn}>← Voltar</button>
                <button type="button" onClick={reenviarCodigo} disabled={loading} style={{ ...linkBtn, color: "var(--accent)" }}>
                  Reenviar código
                </button>
              </div>
            </form>
          ) : null}

          {step === "ok" ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 14, textAlign: "center" }}>
              <div style={{
                width: 64, height: 64, borderRadius: "50%",
                background: "color-mix(in srgb, var(--emerald-500) 20%, transparent)",
                color: "var(--emerald-500)", display: "grid", placeItems: "center",
                fontSize: 32, fontWeight: 800, margin: "0 auto",
              }}>✓</div>
              <div style={{ fontWeight: 700, fontSize: "1.1rem" }}>Conta ativada!</div>
              <p style={{ color: "var(--text-muted)", margin: 0 }}>
                Você já pode entrar com {master ? "seu e-mail de login" : "seu CPF"} e a senha que acabou de criar.
              </p>
              <p style={{ color: "var(--text-dim)", fontSize: ".82rem", margin: 0 }}>
                Redirecionando em alguns segundos…
              </p>
              <Button onClick={() => nav("/login")}>Ir para o login agora →</Button>
            </div>
          ) : null}

          {step !== "ok" ? (
            <div style={{ marginTop: 20, textAlign: "center" }}>
              <Link to="/login" style={{ color: "var(--text-muted)", fontSize: ".88rem" }}>
                Já tenho conta — entrar
              </Link>
            </div>
          ) : null}
        </div>
      </Card>
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const steps: { key: Step; label: string }[] = [
    { key: "cpf", label: "Identificação" },
    { key: "email", label: "Dados" },
    { key: "codigo", label: "Código" },
  ];
  const currentIdx = step === "ok" ? steps.length : steps.findIndex((s) => s.key === step);
  return (
    <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
      {steps.map((s, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <div key={s.key} style={{ display: "flex", alignItems: "center", gap: 8, flex: 1 }}>
            <div
              style={{
                width: 28, height: 28, borderRadius: "50%",
                background: done ? "var(--emerald-500)" : active ? "var(--gold-500)" : "var(--bg-elev-2)",
                color: done || active ? "var(--navy-900)" : "var(--text-muted)",
                display: "grid", placeItems: "center", fontWeight: 700, fontSize: ".82rem",
              }}
            >
              {done ? "✓" : i + 1}
            </div>
            <span style={{ fontSize: ".82rem", color: active ? "var(--text)" : "var(--text-muted)" }}>{s.label}</span>
            {i < steps.length - 1 ? (
              <div style={{ flex: 1, height: 1, background: "var(--border)" }} />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

const linkBtn: React.CSSProperties = { background: "transparent", border: 0, color: "var(--text-muted)", fontSize: ".88rem", cursor: "pointer", padding: 0 };

function ErrorBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: "12px 14px", borderRadius: 10, border: "1px solid var(--danger-500)", background: "color-mix(in srgb, var(--danger-500) 10%, transparent)", color: "var(--text)", fontSize: ".88rem" }}>
      {children}
    </div>
  );
}
function InfoBox({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: "10px 14px", borderRadius: 10, border: "1px dashed var(--gold-500)", background: "color-mix(in srgb, var(--gold-500) 10%, transparent)", color: "var(--text)", fontSize: ".85rem" }}>
      {children}
    </div>
  );
}
function InfoCard({ children }: { children: React.ReactNode }) {
  return (
    <div style={{ padding: 14, borderRadius: 10, background: "var(--bg-elev-2)", border: "1px solid var(--border)" }}>
      {children}
    </div>
  );
}
